// Doc-type catalog, client side — mirrors backend/shared/docTypes.js.
// Platform catalog (docTypes/) + this org's own (orgs/{org}/docTypes/), one
// merged list; org entries shadow platform entries of the same key.
import { useMemo } from 'react';
import { useCollection } from './firestore';
import { createEntity } from './entities';
import { DOC_FAMILIES, DOC_VIEWS, slugDocType } from './deals';

export function useDocTypeCatalog(orgId) {
  const { docs: platform } = useCollection('docTypes');
  const { docs: org } = useCollection(orgId ? `orgs/${orgId}/docTypes` : null);
  return useMemo(() => {
    const byKey = new Map((platform || []).map((e) => [e.key || e.id, { scope: 'platform', ...e, key: e.key || e.id }]));
    for (const e of org || []) byKey.set(e.key || e.id, { scope: 'org', ...e, key: e.key || e.id });
    const list = [...byKey.values()].sort((a, b) => (a.label || '').localeCompare(b.label || ''));
    return { catalog: list, loading: platform === null, byKey };
  }, [platform, org]);
}

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

// Ranked typeahead: key/label/alias prefix & substring hits, then token overlap.
export function searchDocTypes(catalog, query, { limit = 8 } = {}) {
  const q = norm(query);
  if (!q) return catalog.slice(0, limit);
  const qTok = q.split(' ').filter(Boolean);
  const scored = [];
  for (const e of catalog) {
    const label = norm(e.label);
    const hay = [label, e.key, ...(e.aliases || []).map(norm)];
    let score = 0;
    if (label === q || hay.includes(q)) score = 100;
    else if (label.startsWith(q)) score = 80;
    else if (hay.some((h) => h.startsWith(q))) score = 70;
    else if (label.includes(q)) score = 60;
    else if (hay.some((h) => h.includes(q))) score = 50;
    else {
      const lt = new Set(hay.join(' ').split(' '));
      const hit = qTok.filter((t) => lt.has(t)).length;
      if (hit) score = 20 + (hit / qTok.length) * 20;
    }
    if (score) scored.push({ e, score: score + Math.min((e.usageCount || 0) / 100, 5) });
  }
  return scored.sort((a, b) => b.score - a.score).slice(0, limit).map((x) => x.e);
}

// Exact-ish match used to decide whether "Add '…' as a new type" should show.
export function exactDocType(catalog, text) {
  const q = norm(text);
  const key = slugDocType(text);
  return catalog.find((e) => e.key === key || norm(e.label) === q || (e.aliases || []).some((a) => norm(a) === q)) || null;
}

// Coin an org type from the picker. Doc id == key (same as the server).
export async function addOrgDocType(orgId, user, { label, family = 'other', view, description = '' }) {
  const key = slugDocType(label);
  if (!key || key.length < 2) throw new Error('Type name required');
  const fam = DOC_FAMILIES.includes(family) ? family : 'other';
  const data = {
    key,
    label: String(label).trim().slice(0, 80),
    family: fam,
    view: DOC_VIEWS.includes(view) ? view : (fam === 'agreement' || fam === 'financing' ? 'agreement' : 'general'),
    description: String(description || '').slice(0, 400),
    aliases: [],
    source: 'org',
    usageCount: 0,
    createdBy: user.uid,
    createdAt: Date.now(),
    updatedAt: Date.now(),
  };
  await createEntity('orgDocTypeDef', { org: orgId }, data, { id: key });
  return { id: key, scope: 'org', orgId, ...data };
}

// Family → sensible default renderer view for a freshly coined type.
export const familyDefaultView = (family) => (family === 'agreement' || family === 'financing' ? 'agreement' : 'general');
